import React, {
  useState,
  useRef,
  useEffect,
  forwardRef,
  useImperativeHandle,
  useCallback,
} from "react";
import { UPPER_ARCH_PATH, LOWER_ARCH_PATH, BOTH_ARCH_PATH, TOOTH_PATHS, TOOTH_ORDER } from "./dentalArchGeometry";

const DentalChart = forwardRef(
  ({ initialHighlights = {}, onHighlightChange, style, ...props }, ref) => {
    const svgRef = useRef(null);
    // State voor de kleuren van de tanden
    const [highlightedTeeth, setHighlightedTeeth] = useState(initialHighlights);
    
    // Nieuwe state voor cache invalidatie
    const [highlightsVersion, setHighlightsVersion] = useState(0);
    
    // Cache referentie voor SVG resultaten
    const cacheRef = useRef(new Map());
    
 
    
    useEffect(() => {
      const newHighlights = { ...initialHighlights };
 
      if (newHighlights.Upper) {
        setUpperColorState(newHighlights.Upper);
        setIsUpperHighlighted(true);
        delete newHighlights.Upper;
      } else {
        // Reset naar default als er geen Upper is
        setUpperColorState(DEFAULT_COLOR);
        setIsUpperHighlighted(false);
      }
      if (newHighlights.Lower) {
        setLowerColorState(newHighlights.Lower);
        setIsLowerHighlighted(true);
        delete newHighlights.Lower;
      } else {
        // Reset naar default als er geen Lower is (bugfix: was true, nu false)
        setLowerColorState(DEFAULT_COLOR);
        setIsLowerHighlighted(false);
      }
      // BELANGRIJK: Vervang ALLE highlights, niet mergen!
      setHighlightedTeeth(newHighlights);
      // Increment version voor cache invalidatie bij prop changes
      setHighlightsVersion(prev => prev + 1);
    }, [initialHighlights]);
    
    // State voor Upper en Lower kleuren (hernoemd voor interne setters)
    const [upperColorState, setUpperColorState] = useState("rgba(255, 255, 255, 0.1)");
    const [lowerColorState, setLowerColorState] = useState("rgba(255, 255, 255, 0.1)");
    const unselectedFillColor = "rgba(255 255, 255, 0.2)";
    const unselectedStrokeColor = "rgba(255, 255, 255, 0.3)";
    const [isUpperHighlighted, setIsUpperHighlighted] = useState(false);
    const [isLowerHighlighted, setIsLowerHighlighted] = useState(false);
    
    // Gebruik de state voor rendering (upperColor verwijst nu naar state)
    const upperColor = upperColorState;
    const lowerColor = lowerColorState;
  const DEFAULT_COLOR = "rgba(255, 255, 255, 0.1)";

    
    // Wrapped setters voor upper/lower met version increment
    const handleUpperColorChange = useCallback((color) => {
      setUpperColorState(color);
      setIsUpperHighlighted(color !== DEFAULT_COLOR);
      setHighlightsVersion(prev => prev + 1);
    }, []);
    
    const handleLowerColorChange = useCallback((color) => {
      setLowerColorState(color);
      setIsLowerHighlighted(color !== DEFAULT_COLOR);
      setHighlightsVersion(prev => prev + 1);
    }, []);
    
    // Functie om een tand te highlighten
    const highlightTooth = useCallback((toothNumber, color = "#4CAF50") => {
      setHighlightedTeeth((prev) => ({
        ...prev,
        [toothNumber]: color,
      }));
      // Increment version voor cache invalidatie
      setHighlightsVersion(prev => prev + 1);
    }, []);
    
    // Functie om een tand highlight te verwijderen
    const unhighlightTooth = useCallback((toothNumber) => {
      setHighlightedTeeth((prev) => {
        const newState = { ...prev };
        delete newState[toothNumber];
        return newState;
      });
      // Increment version voor cache invalidatie
      setHighlightsVersion(prev => prev + 1);
    }, []);
    
    // Functie om alle highlights te wissen
    const clearAllHighlights = useCallback(() => {
      setHighlightedTeeth({});
      // Increment version voor cache invalidatie
      setHighlightsVersion(prev => prev + 1);
    }, []);
    
    // Functie om de SVG te exporteren voor PDF
    const exportForPDF = useCallback(() => {
      if (!svgRef.current) return null;
      // Clone de SVG
      const svgClone = svgRef.current.cloneNode(true);
      // Serialize de SVG naar string
      const serializer = new XMLSerializer();
      const svgString = serializer.serializeToString(svgClone);
      // Return als data URL
      const blob = new Blob([svgString], { type: "image/svg+xml" });
      const url = URL.createObjectURL(blob);
      return {
        svgString,
        dataUrl: url,
        blob,
      };
    }, []);
    
    // Download functie (voor demo)
    const downloadSVG = useCallback(() => {
      const { dataUrl } = exportForPDF();
      if (dataUrl) {
        const link = document.createElement("a");
        link.href = dataUrl;
        link.download = "tandenschema.svg";
        link.click();
      }
    }, [exportForPDF]);
    
    // Cache clear effect bij version change
    useEffect(() => {
      cacheRef.current.clear();
    }, [highlightsVersion]);
    


    // ✅ useCallback met dependency op highlightsVersion voor up-to-date access
    const getToothSVG = useCallback((toothNumber) => {
      if (!svgRef.current) {
        console.warn("svgRef.current is null");
        return null;
      }
      
      const cacheKey = `${toothNumber}-${highlightsVersion}`;
      if (cacheRef.current.has(cacheKey)) {
        return cacheRef.current.get(cacheKey);
      }
      
      const toothGroup = svgRef.current.querySelector(`[id="${toothNumber}"]`);
      if (!toothGroup) {
        console.warn(`Tooth group not found for tooth ${toothNumber}`);
        return null;
      }
      // ✅ Bereken bbox VAN DE ORIGINELE GROUP (niet van clone!)
      let bbox;
      try {
        bbox = toothGroup.getBBox();
      } catch (err) {
        console.warn(`BBox error for tooth ${toothNumber}:`, err);
        bbox = { x: 0, y: 0, width: 50, height: 50 };
      }
      // ✅ Validate bbox - moet redelijke waarden hebben
      if (bbox.width < 1 || bbox.height < 1 || (bbox.x === 0 && bbox.y === 0)) {
        console.warn(`Invalid BBox for tooth ${toothNumber}:`, bbox);
        // Probeer bbox van de path binnen de group
        const pathElement = toothGroup.querySelector("path");
        if (pathElement) {
          try {
            bbox = pathElement.getBBox();
          } catch (err) {
            console.error(`Path BBox also failed:`, err);
            return null;
          }
        }
      }
      // ✅ Laatste check
      if (bbox.width < 1 || bbox.height < 1) {
        console.error(`Still invalid bbox for tooth ${toothNumber}, aborting`);
        return null;
      }
      // ✅ Nu pas clonen
      const clone = toothGroup.cloneNode(true);
      const newSVG = document.createElementNS(
        "http://www.w3.org/2000/svg",
        "svg"
      );
      const padding = 8;
      const viewBoxStr = `${bbox.x - padding} ${bbox.y - padding} ${
        bbox.width + 2 * padding
      } ${bbox.height + 2 * padding}`;
      newSVG.setAttribute("viewBox", viewBoxStr);
      newSVG.setAttribute("width", "100");
      newSVG.setAttribute("height", "100");
      newSVG.setAttribute("xmlns", "http://www.w3.org/2000/svg");
      newSVG.appendChild(clone);
      const serializer = new XMLSerializer();
      const svgString = serializer.serializeToString(newSVG);
      const encoded = encodeURIComponent(svgString).replace(
        /%([0-9A-F]{2})/g,
        (match, p1) => {
          return String.fromCharCode(parseInt(p1, 16));
        }
      );
      const dataUrl = `data:image/svg+xml;charset=utf-8;base64,${btoa(
        encoded
      )}`;
      const result = {
        svgString,
        dataUrl,
      };
      // Cache het resultaat
      cacheRef.current.set(cacheKey, result);
      return result;
    }, [highlightsVersion]); // Dependency op version voor up-to-date key
    
    // Expose functies via ref (nu met wrapped upper/lower handlers)
    useImperativeHandle(ref, () => ({
      highlightTooth,
      unhighlightTooth,
      clearAllHighlights,
      setUpperColor: handleUpperColorChange, // Gebruik wrapped versie
      setLowerColor: handleLowerColorChange, // Gebruik wrapped versie
      exportForPDF,
      downloadSVG,
      getToothSVG, // Nu met caching
    }));
    
    const spacer = 20;
    const start = 20;
    const end = 376;
    // Uniforme posities voor labels
    const leftLabels = [
      { num: "11", y: start },
      { num: "12", y: start + 1 * spacer },
      { num: "13", y: start + 2 * spacer },
      { num: "14", y: start + 3 * spacer },
      { num: "15", y: start + 4 * spacer },
      { num: "16", y: start + 5 * spacer },
      { num: "17", y: start + 6 * spacer },
      { num: "18", y: start + 7 * spacer },
      { num: "41", y: end },
      { num: "42", y: end - 1 * spacer },
      { num: "43", y: end - 2 * spacer },
      { num: "44", y: end - 3 * spacer },
      { num: "45", y: end - 4 * spacer },
      { num: "46", y: end - 5 * spacer },
      { num: "47", y: end - 6 * spacer },
      { num: "48", y: end - 7 * spacer },
    ];
    const rightLabels = [
      { num: "21", y: start },
      { num: "22", y: start + 1 * spacer },
      { num: "23", y: start + 2 * spacer },
      { num: "24", y: start + 3 * spacer },
      { num: "25", y: start + 4 * spacer },
      { num: "26", y: start + 5 * spacer },
      { num: "27", y: start + 6 * spacer },
      { num: "28", y: start + 7 * spacer },
      { num: "31", y: end },
      { num: "32", y: end - 1 * spacer },
      { num: "33", y: end - 2 * spacer },
      { num: "34", y: end - 3 * spacer },
      { num: "35", y: end - 4 * spacer },
      { num: "36", y: end - 5 * spacer },
      { num: "37", y: end - 6 * spacer },
      { num: "38", y: end - 7 * spacer },
    ];
    // Functie om een label te renderen
    const renderLabel = (num, y, side) => {
      const isHighlighted = !!highlightedTeeth[num];
      const fillColor = isHighlighted ? "white" : "rgba(255,255,255,0.4)";
      const textX = side === "left" ? 16 : 238;
      return (
        <g key={num}>
          <text
            x={textX}
            y={y}
            fill={fillColor}
            fontSize="8"
            fontFamily="'DM Mono', monospace"
            fontWeight="500"
            style={{ lineHeight: "8px" }}
            textAnchor="middle"
            dominantBaseline="middle"
          >
            {num}
          </text>
          {isHighlighted &&
            (side === "left" ? (
              <line x1="0" y1={y} x2="6" y2={y} stroke="grey" strokeWidth="1" />
            ) : (
              <line
                x1="250"
                y1={y}
                x2="256"
                y2={y}
                stroke="grey"
                strokeWidth="1"
              />
            ))}
        </g>
      );
    };

    return (
      <svg
        ref={svgRef}
        width="256"
        height="400"
        viewBox="0 0 256 400"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        style={style}
        {...props}
      >
        <g id="Arch">
          <rect
            x="0.5"
            y="0.5"
            width="255"
            height="399"
            rx="7.5"
            stroke="grey"
            strokeOpacity="1.0"
          />
          {/* Labels */}
          <g id="labels">
            {leftLabels.map(({ num, y }) => renderLabel(num, y, "left"))}
            {rightLabels.map(({ num, y }) => renderLabel(num, y, "right"))}
          </g>
          {/* Both */}
          <g id="Both">
            <path
              d={BOTH_ARCH_PATH}
           
            
    
    
               fill=  "rgba(255, 255, 255, 0.1)"
              stroke={DEFAULT_COLOR}
              strokeOpacity="1.0"
                 strokeWidth={isUpperHighlighted ? 3 : 1}
       
            />
          </g>
          {/* Upper */}
          <g id="Upper">
            <path
              d={UPPER_ARCH_PATH}
              fill=  "rgba(255, 255, 255, 0.1)"
              stroke={upperColor}
              strokeOpacity="1.0"
                 strokeWidth={isUpperHighlighted ? 3 : 1}
       
            />
          </g>
          {/* Lower */}
          <g id="Lower">
            <path
              d={LOWER_ARCH_PATH}
             fill=  "rgba(255, 255, 255, 0.1)"
                  stroke={lowerColor}
              strokeOpacity="1.0"
                  strokeWidth={isLowerHighlighted ? 3 : 1}
            />
          </g>
          {/* Tanden - alleen de gedefinieerde nummers */}
          {TOOTH_ORDER.map((n) => (
            <g id={String(n)} key={n}>
              <path
                d={TOOTH_PATHS[n]}
                fill={highlightedTeeth[String(n)] || unselectedFillColor}
                stroke={highlightedTeeth[String(n)] || unselectedStrokeColor}
              />
            </g>
          ))}
        </g>
      </svg>
    );
  }
);

DentalChart.displayName = "DentalChart";

export default DentalChart;
